import { useState, useEffect } from "react";
import { X, Save, Loader2 } from "lucide-react";
import { api } from "../lib/api";

// PC IP-configuration window. Mirrors the Packet Tracer "IP Configuration"
// applet: interface dropdown, DHCP / Static radio, conditional address
// fields, current-config summary at the top, Apply button that POSTs to
// /labs/session/{id}/pc-config.
export default function PcConfigModal({ open, onClose, sessionId, deviceId, deviceLabel, initial }) {
  const [iface, setIface] = useState("Ethernet0");
  const [mode, setMode] = useState("static"); // "static" | "dhcp"
  const [ip, setIp] = useState("");
  const [mask, setMask] = useState("");
  const [gateway, setGateway] = useState("");
  const [dns, setDns] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // Snapshot of "what's currently applied" for the summary panel up top.
  const [current, setCurrent] = useState({ ip: "", mask: "", gateway: "", mode: "static" });

  useEffect(() => {
    if (!open) return;
    const initIp = initial?.ip || "";
    const initMask = initial?.mask || "";
    const initGw = initial?.gateway || "";
    const initMode = initIp || initMask || initGw ? "static" : "dhcp";
    setIface("Ethernet0");
    setMode(initMode);
    setIp(initIp);
    setMask(initMask);
    setGateway(initGw);
    setDns("");
    setError(null);
    setCurrent({ ip: initIp, mask: initMask, gateway: initGw, mode: initMode });
  }, [open, initial]);

  const validIp = (v) => {
    if (!v) return true;
    const parts = v.split(".");
    if (parts.length !== 4) return false;
    return parts.every((p) => /^\d+$/.test(p) && Number(p) >= 0 && Number(p) <= 255);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (mode === "static") {
      if (!validIp(ip) || !validIp(mask) || !validIp(gateway) || !validIp(dns)) {
        setError("Each field must be a valid IPv4 address (or blank)");
        return;
      }
    }
    setSaving(true);
    setError(null);
    try {
      const payload = mode === "dhcp"
        ? { device_id: deviceId, ip: "", mask: "", gateway: "" }
        : { device_id: deviceId, ip, mask, gateway };
      await api.post(`/labs/session/${sessionId}/pc-config`, payload);
      setCurrent({
        ip: payload.ip,
        mask: payload.mask,
        gateway: payload.gateway,
        mode,
      });
      onClose?.({ ip: payload.ip, mask: payload.mask, gateway: payload.gateway });
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;
  const dash = "(none)";
  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="pc-config-modal"
      onClick={() => onClose?.()}
    >
      <div
        className="bg-[#141416] border border-zinc-800 max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#00E5FF] font-mono">
              PC IP configuration
            </div>
            <div className="font-display font-bold text-base mt-1">{deviceLabel || deviceId}</div>
          </div>
          <button
            onClick={() => onClose?.()}
            className="p-1 text-zinc-500 hover:text-white"
            data-testid="pc-config-close"
          >
            <X size={16} />
          </button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-4">
          {/* Current config summary */}
          <div
            className="bg-black/40 border border-zinc-800 px-3 py-2.5 font-mono text-xs space-y-0.5"
            data-testid="pc-config-current"
          >
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">
              current configuration
            </div>
            <Row k="mode" v={current.mode === "dhcp" ? "DHCP" : "Static"} />
            <Row k="ipv4" v={current.ip || dash} />
            <Row k="mask" v={current.mask || dash} />
            <Row k="gateway" v={current.gateway || dash} />
          </div>

          {/* Interface picker */}
          <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.25em] font-mono text-zinc-500 mb-1.5">
              interface
            </span>
            <select
              value={iface}
              onChange={(e) => setIface(e.target.value)}
              className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-sm focus:border-[#00E5FF] outline-none"
              data-testid="pc-config-interface"
            >
              <option value="Ethernet0">Ethernet0</option>
            </select>
          </label>

          {/* DHCP / Static */}
          <fieldset className="flex items-center gap-4 text-xs font-mono uppercase tracking-widest">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="ipmode"
                value="dhcp"
                checked={mode === "dhcp"}
                onChange={() => setMode("dhcp")}
                className="accent-[#00E5FF]"
                data-testid="pc-config-mode-dhcp"
              />
              DHCP
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="ipmode"
                value="static"
                checked={mode === "static"}
                onChange={() => setMode("static")}
                className="accent-[#00E5FF]"
                data-testid="pc-config-mode-static"
              />
              Static
            </label>
          </fieldset>

          {mode === "static" && (
            <div className="space-y-3" data-testid="pc-config-static-fields">
              <Field label="IPv4 address" value={ip} onChange={setIp} placeholder="192.168.1.10" testid="pc-ip" />
              <Field label="Subnet mask" value={mask} onChange={setMask} placeholder="255.255.255.0" testid="pc-mask" />
              <Field label="Default gateway" value={gateway} onChange={setGateway} placeholder="192.168.1.1" testid="pc-gateway" />
              <Field label="DNS server" value={dns} onChange={setDns} placeholder="8.8.8.8" testid="pc-dns" />
            </div>
          )}

          {error && (
            <div className="text-xs text-red-400 font-mono" data-testid="pc-config-error">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => onClose?.()}
              className="px-3 py-2 text-xs uppercase tracking-widest font-mono text-zinc-300 hover:text-white"
            >
              cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-3 py-2 bg-[#00E5FF] text-black font-semibold flex items-center gap-2 text-xs uppercase tracking-widest disabled:opacity-50"
              data-testid="pc-config-save"
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} apply
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between text-zinc-300">
      <span className="text-zinc-500">{k}</span>
      <span>{v}</span>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testid }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.25em] font-mono text-zinc-500 mb-1.5">
        {label}
      </span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-sm focus:border-[#00E5FF] outline-none"
        data-testid={testid}
        spellCheck={false}
        autoCorrect="off"
        autoCapitalize="off"
      />
    </label>
  );
}
